import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';

import { IconComponent } from './icon.component';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { UI_REASONS } from './ui-reasons';
import { setOneOf, setText, type UiInspector } from '@sharedlib/sheets-editor/ui-inspector';
import type { SheetsUiValue } from '@sharedlib/common/sheets/ui-commands';

/** The panel's own words, from sheets.strings.ts (NFR-8, AM-113). */
export const SPLIT_TEXT = {
  title: SHEETS_STRINGS['Split text to columns'],
  separator: SHEETS_STRINGS.Separator,
  custom: SHEETS_STRINGS['Custom separator'],
  split: SHEETS_STRINGS.Split,
} as const;

/** What each choice splits on: the four `range.split_text` names its delimiter (§7.7.5 op 30), or the user's own. */
export const SPLIT_SEPARATORS = [
  { key: 'comma', label: SHEETS_STRINGS.Comma, delimiter: ',' },
  { key: 'semicolon', label: SHEETS_STRINGS.Semicolon, delimiter: ';' },
  { key: 'tab', label: SHEETS_STRINGS.Tab, delimiter: '\t' },
  { key: 'space', label: SHEETS_STRINGS.Space, delimiter: ' ' },
  { key: 'custom', label: SHEETS_STRINGS.Custom, delimiter: '' },
] as const;

/**
 * §11.6.3 #31's "Split text to columns…" (B-177). Engine 0.25.2 has no dialog for it, and its
 * command needs the separator, so this asks for one, as Stitchy's `range.split_text` takes it: a
 * comma, semicolon, tab or space, or a character of the user's own. The engine splits on one custom
 * character only, so that is all the field takes (and Stitchy refuses longer ones by name). It
 * emits the separator.
 */
@Component({
  selector: 'app-split-panel',
  standalone: true,
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./sheets-panels.css', './sheets-buttons.css'],
  templateUrl: './split-panel.component.html',
})
export class SplitPanelComponent implements UiInspector {
  @Input() public range = '';
  @Output() public readonly split = new EventEmitter<string>();
  @Output() public readonly dismiss = new EventEmitter<void>();

  protected readonly S = SHEETS_STRINGS;
  protected readonly T = SPLIT_TEXT;
  protected readonly separators = SPLIT_SEPARATORS;
  protected readonly choice = signal<(typeof SPLIT_SEPARATORS)[number]['key']>('comma');
  protected readonly custom = signal('');

  /** The separator to split on, or null while "Custom" has none. */
  protected delimiter(): string | null {
    if (this.choice() !== 'custom') return SPLIT_SEPARATORS.find((s) => s.key === this.choice())!.delimiter;
    return this.custom().length === 1 ? this.custom() : null;
  }

  protected choose(event: Event): void {
    this.choice.set((event.target as HTMLSelectElement).value as (typeof SPLIT_SEPARATORS)[number]['key']);
  }

  protected setCustom(event: Event): void {
    this.custom.set((event.target as HTMLInputElement).value);
  }

  protected emitSplit(): void {
    const delimiter = this.delimiter();
    if (delimiter !== null) this.split.emit(delimiter);
  }

  // ------------------------------------------- sheets_ui (MCP contract, 6 Oct: "set fields … apply/cancel")

  public uiFields(): Readonly<Record<string, unknown>> {
    return { range: this.range, separator: this.choice(), custom: this.custom() };
  }

  public uiSet(field: string, value: SheetsUiValue): string | null {
    if (field === 'separator') {
      return setOneOf(
        field,
        value,
        SPLIT_SEPARATORS.map((s) => s.key),
        (v) => this.choice.set(v as (typeof SPLIT_SEPARATORS)[number]['key']),
      );
    }
    // The field takes one character: the engine splits on one custom character only (B-177).
    if (field === 'custom') return typeof value === 'string' && value.length > 1 ? UI_REASONS.oneCharacter(field) : setText(field, value, (v) => this.custom.set(v));
    return UI_REASONS.noField('split', field);
  }

  /** "Split", which is disabled until there is a separator. */
  public uiApply(): string | null {
    if (this.delimiter() === null) return UI_REASONS.splitDisabled;
    this.emitSplit();
    return null;
  }

  public uiCancel(): void {
    this.dismiss.emit();
  }
}
